<script setup lang="ts">
import { computed } from 'vue'
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui'

import {
  SEARCH_ENTITY_ICON,
  SEARCH_ENTITY_TYPE_LABEL,
  type SearchEntityType,
} from '~/constants/searchEntityMeta'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'
import { useOverlayPopperZ } from '~/composables/ui/useOverlayStack'

const SourceChip = defineAsyncWithSkeleton(
  function loadSourceChip() {
    return import('~/components/ui/SourceChip.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

const overlayPopperZ = useOverlayPopperZ()

const props = defineProps<{
  scopeTypes: SearchEntityType[]
  availableTypes: SearchEntityType[]
}>()

const emit = defineEmits<{
  (event: 'toggle-type', entityType: SearchEntityType): void
  (event: 'clear'): void
}>()

const scopeLabel = computed(function resolveScopeLabel() {
  if (props.scopeTypes.length === 0) {
    return 'Tout'
  }
  if (props.scopeTypes.length === 1) {
    const singleType = props.scopeTypes[0]
    if (singleType) {
      return SEARCH_ENTITY_TYPE_LABEL[singleType] ?? singleType
    }
  }
  return `${props.scopeTypes.length} entités`
})

function isTypeActive(entityType: SearchEntityType): boolean {
  return props.scopeTypes.includes(entityType)
}

function onToggleType(entityType: SearchEntityType, clickEvent: MouseEvent): void {
  clickEvent.preventDefault()
  clickEvent.stopPropagation()
  emit('toggle-type', entityType)
}

function onClearScope(clickEvent: MouseEvent): void {
  clickEvent.preventDefault()
  clickEvent.stopPropagation()
  emit('clear')
}
</script>

<template>
  <PopoverRoot>
    <PopoverTrigger
      class="global-search-scope__trigger"
      type="button"
      aria-label="Choisir les types d'entités à rechercher"
      @click.stop
    >
      <span class="global-search-scope__trigger-label">{{ scopeLabel }}</span>
      <span class="material-symbols-rounded global-search-scope__trigger-icon" aria-hidden="true">
        expand_more
      </span>
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        class="global-search-scope__menu"
        side="bottom"
        :align="'start'"
        :side-offset="6"
        :style="{ zIndex: overlayPopperZ }"
        @click.stop
      >
        <div class="global-search-scope__menu-header">
          <span class="global-search-scope__menu-title">Rechercher dans</span>
          <button
            type="button"
            class="global-search-scope__clear-btn"
            :disabled="scopeTypes.length === 0"
            @click="onClearScope($event)"
          >
            Tout réinitialiser
          </button>
        </div>
        <div class="global-search-scope__chips" role="group" aria-label="Types d'entités">
          <SourceChip
            v-for="entityType in availableTypes"
            :key="entityType"
            :text="SEARCH_ENTITY_TYPE_LABEL[entityType]"
            :icon="SEARCH_ENTITY_ICON[entityType]"
            :is-on="isTypeActive(entityType)"
            size="small"
            @click="onToggleType(entityType, $event)"
          />
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
